/**
 * Store Locator
 *
 * Two independent pieces live in this file:
 *
 * 1. StoreLocator custom element — handles merchant-pasted store blocks
 *    (theme editor `item` blocks with a map embed iframe per block).
 *    Clicking a listing swaps the map pane's iframe.
 *
 * 2. Data-driven store list — fetches assets/stores.json and renders the
 *    full store directory client-side, with text search filtering by
 *    store name, address line (town) and postcode.
 */
class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.item = this.querySelectorAll('.item');
    this.map = this.querySelector('#map');

    if (this.item) this.item.forEach((item) => { item.addEventListener('click', this.onButtonClick.bind(this)); });
  }

  onButtonClick(event) {
    const target = event.currentTarget;
    const map = target.dataset.map;
    if (!this.map || !map) return;

    const active = this.querySelector('.active');
    if (active) active.classList.remove('active');
    target.classList.add('active');

    this.map.classList.add('map-loading');
    setTimeout(() => { this.map.innerHTML = map; }, 300);
    setTimeout(() => { this.map.classList.remove('map-loading'); }, 1200);
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}

(function () {
  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  /*
    WP 9.3 review: postcode search only matched when the shopper's spacing
    happened to match the stored format, so "w1c1dx" failed to find a store
    stored as "W1C 1DX". The haystack now carries a whitespace-stripped copy
    alongside the normal text, and the query is stripped the same way, so
    both spellings match.
  */
  function buildSearchText(store) {
    var parts = [store.name || ''].concat(store.address_lines || [], [store.postcode || '']);
    var text = parts.join(' ').toLowerCase();
    return text + ' ' + text.replace(/\s+/g, '');
  }

  function renderHours(hours) {
    if (!hours || !hours.length) return '';
    var rows = hours.map(function (row) {
      return escapeHtml(row.day) + ': ' + escapeHtml(row.time);
    }).join('<br>');
    return '<div class="text store-locator__hours">' + rows + '</div>';
  }

  function renderPhone(phone) {
    if (!phone || !phone.display) return '';
    var tel = (phone.tel || phone.display).replace(/\s+/g, '');
    return '<div class="text store-locator__phone">Tel: <a href="tel:' + escapeHtml(tel) + '">' + escapeHtml(phone.display) + '</a></div>';
  }

  function renderStoreCard(store) {
    var addressHtml = (store.address_lines || []).map(escapeHtml).join('<br>');
    if (store.postcode) {
      addressHtml += (addressHtml ? '<br>' : '') + escapeHtml(store.postcode);
    }

    var noteHtml = store.note ? '<p class="text store-locator__note">' + escapeHtml(store.note) + '</p>' : '';

    var card = document.createElement('div');
    card.className = 'item';
    card.setAttribute('data-store', '');
    card.setAttribute('data-store-search', buildSearchText(store));
    card.innerHTML =
      '<h2 class="title">' + escapeHtml(store.name) + '</h2>' +
      noteHtml +
      '<div class="text">' + addressHtml + '</div>' +
      renderPhone(store.phone) +
      renderHours(store.hours);
    return card;
  }

  function updateCount(countEl, visible, total) {
    if (!countEl) return;
    countEl.textContent = visible + ' of ' + total + ' stores';
  }

  function initSearch(root, cards) {
    var input = root.querySelector('[data-store-locator-input]');
    var countEl = root.querySelector('[data-store-locator-count]');
    var emptyEl = root.querySelector('[data-store-locator-empty]');
    var total = cards.length;

    function filter(query) {
      var raw = (query || '').trim().toLowerCase();
      var q = raw;
      var qCompact = raw.replace(/\s+/g, '');
      var visible = 0;
      cards.forEach(function (card) {
        var haystack = card.getAttribute('data-store-search') || '';
        var match =
          q === '' ||
          haystack.indexOf(q) !== -1 ||
          (qCompact !== '' && haystack.indexOf(qCompact) !== -1);
        if (match) {
          card.removeAttribute('hidden');
          visible++;
        } else {
          card.setAttribute('hidden', '');
        }
      });
      if (emptyEl) {
        if (visible === 0) emptyEl.removeAttribute('hidden');
        else emptyEl.setAttribute('hidden', '');
      }
      updateCount(countEl, visible, total);
    }

    if (input) {
      input.addEventListener('input', function (event) {
        filter(event.target.value);
      });
    }

    updateCount(countEl, total, total);
  }

  /*
    WP 9.3 review: this previously swallowed every fetch failure in an empty
    catch and returned before initSearch() when the list came back empty. A
    CDN blip, an ad-blocker or a bad deploy therefore produced a blank panel
    with a search box that had no listener attached and no message of any
    kind -- a silently dead Find a Store page. Failures are now surfaced, and
    search is always wired up.
  */
  function showLocatorMessage(root, message) {
    var empty = root.querySelector('[data-store-locator-empty]');
    if (!empty) return;
    empty.textContent = message;
    empty.hidden = false;
  }

  function initStoreLocatorList(root) {
    var grid = root.querySelector('[data-store-locator-grid]');
    var url = root.getAttribute('data-stores-url');
    if (!grid || !url || grid.hasChildNodes()) return;

    root.setAttribute('data-store-locator-state', 'loading');

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var stores = (data && data.stores) || [];

        if (!stores.length) {
          root.setAttribute('data-store-locator-state', 'empty');
          showLocatorMessage(root, 'Store information is unavailable right now. Please try again shortly.');
          initSearch(root, grid.querySelectorAll('[data-store]'));
          return;
        }

        var fragment = document.createDocumentFragment();
        stores.forEach(function (store) {
          fragment.appendChild(renderStoreCard(store));
        });
        grid.appendChild(fragment);

        root.setAttribute('data-store-locator-state', 'ready');
        initSearch(root, grid.querySelectorAll('[data-store]'));
      })
      .catch(function () {
        root.setAttribute('data-store-locator-state', 'error');
        showLocatorMessage(root, 'We could not load our store list. Please refresh the page or try again shortly.');
        initSearch(root, grid.querySelectorAll('[data-store]'));
      });
  }

  function initAll() {
    document.querySelectorAll('[data-store-locator]').forEach(initStoreLocatorList);
  }

  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-store-locator]');
    if (root) initStoreLocatorList(root);
  });
})();
